import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import EditorAddIcon from '@atlaskit/icon/core/add';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg as platform_fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { ToolbarDropdownItem, PlusIcon } from '@atlaskit/editor-toolbar';

import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';
import { expValEquals } from '@confluence/feature-experiments';

import { createTrelloCardHandle } from './createTrelloInboxHandlers';

const logger = getLogger('highlight-action');

type AddToTrelloInboxActionsProps = {
	activeSelection?: Selection | null;
	completeKey: string;
	contentId: string;
};

const getHighlightedText = (selection?: Selection | null): string => {
	if (!selection || selection.rangeCount === 0) {
		return '';
	}
	return selection.toString().trim();
};

export const AddToTrelloInboxActions = ({
	activeSelection,
	completeKey,
	contentId,
}: AddToTrelloInboxActionsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireTrelloCardCreatedAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'confluenceHighlightActionsMenu',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'trelloInboxCard',
				type: 'ui',
				attributes: {
					contentId,
					hasSelection: Boolean(activeSelection && activeSelection.toString().trim()),
				},
			},
		}).fire();
	}, [createAnalyticsEvent, contentId, activeSelection]);

	const handleCreateTrelloCardClick = useCallback(async () => {
		const highlightedText = getHighlightedText(activeSelection);

		if (!highlightedText) {
			return;
		}

		try {
			fireTrelloCardCreatedAnalytics();
			await createTrelloCardHandle({
				text: highlightedText,
				contentId,
			});
		} catch (error) {
			logger.error`Error creating Trello card for contentId=${contentId}`;
		}
	}, [activeSelection, contentId, fireTrelloCardCreatedAnalytics]);

	if (!fg('quick_capture_trello_inbox')) {
		return null;
	}

	if (
		fg('aifc_create_enabled') ||
		expValEquals('platform_editor_toolbar_aifc_confluence', 'isEnabled', true)
	) {
		return (
			<ToolbarDropdownItem
				testId="create-trello-card-button"
				data-key={completeKey}
				shouldTitleWrap={false}
				elemBefore={<PlusIcon size="small" label="" />}
				onClick={handleCreateTrelloCardClick}
			>
				<FormattedMessage
					id="highlight-actions.create-trello-card"
					defaultMessage="Send to your Trello Inbox"
					description="Create a Trello card from highlighted text"
				/>
			</ToolbarDropdownItem>
		);
	}

	return editorExperiment('platform_editor_controls', 'variant1') &&
		platform_fg('platform_editor_controls_view_selection_toolbar') ? (
		<DropdownItem
			testId="create-trello-card-button"
			data-key={completeKey}
			shouldTitleWrap={false}
			elemBefore={<EditorAddIcon label="" />}
			onClick={handleCreateTrelloCardClick}
		>
			<FormattedMessage
				id="highlight-actions.create-trello-card"
				defaultMessage="Send to your Trello Inbox"
				description="Create a Trello card from highlighted text"
			/>
		</DropdownItem>
	) : (
		<Button
			id="create-trello-card-button"
			appearance="subtle"
			iconBefore={(iconProps) => <EditorAddIcon {...iconProps} label="" />}
			data-key={completeKey}
			testId="create-trello-card-button"
			onClick={handleCreateTrelloCardClick}
			spacing="compact"
		>
			<FormattedMessage
				id="highlight-actions.create-trello-card"
				defaultMessage="Send to your Trello Inbox"
				description="Create a Trello card from highlighted text"
			/>
		</Button>
	);
};
